import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, Search, ShieldCheck, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { emailCategories, emailProviders } from "@/mocks/email";
import { t, tn } from "@/lib/i18n";

/* Живой макет «Подключение почты»: слева подключённые ящики, справа каталог провайдеров.
   Подключение имитируется (авторизация у провайдера или проверка IMAP/SMTP) — реальные ящики не затрагиваются. */

type Kind = "oauth" | "imap";
type Hosts = { imap: string; imapPort: string; smtp: string; smtpPort: string };
type Provider = {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  badge?: string;
  kind: Kind;
  authLabel?: string;
  sample: string;
  connected: boolean;
  account: string;
  unread: number;
  permissions: string[];
  hosts?: Hosts;
};
type Step = { label: string; state: "wait" | "run" | "ok" };

const SOURCE = emailProviders as unknown as Provider[];
const EMPTY_HOSTS: Hosts = { imap: "", imapPort: "993", smtp: "", smtpPort: "465" };
const SECURE = ["SSL/TLS", "STARTTLS", t("Без шифрования")];
const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

const FIELD =
  "h-10 w-full box-border min-w-0 rounded-btn border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

function EmailDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [items, setItems] = useState<Provider[]>(() => SOURCE.map((p) => ({ ...p })));
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<Provider | null>(null);
  const [mail, setMail] = useState("");
  const [pass, setPass] = useState("");
  const [sender, setSender] = useState("");
  const [hosts, setHosts] = useState<Hosts>(EMPTY_HOSTS);
  const [secure, setSecure] = useState(SECURE[0]);
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [toast, setToast] = useState("");
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 4600);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((p) => {
      if (cat !== "all" && p.category !== cat) return false;
      if (!q) return true;
      return `${p.name} ${p.description}`.toLowerCase().includes(q);
    });
  }, [items, cat, query]);

  const connected = items.filter((p) => p.connected);
  const countIn = (id: string) => (id === "all" ? items.length : items.filter((p) => p.category === id).length);

  const open = (p: Provider) => {
    setDraft(p);
    setMail("");
    setPass("");
    setSender("");
    setHosts(p.hosts ? { ...p.hosts } : { ...EMPTY_HOSTS });
    setSecure(SECURE[0]);
    setSteps([]);
    setBusy(false);
  };

  const close = () => {
    if (busy) return;
    setDraft(null);
    setSteps([]);
  };

  const finish = (target: Provider, account: string) => {
    setItems((prev) => prev.map((p) => (p.id === target.id ? { ...p, connected: true, account, unread: 0 } : p)));
    setBusy(false);
    setSteps([]);
    setDraft(null);
    flash(t("{name}: ящик {account} подключён — новые письма идут во «Входящие»", { name: target.name, account }));
  };

  const connectOauth = async () => {
    if (!draft || busy) return;
    setBusy(true);
    await wait(1000);
    finish(draft, draft.sample);
  };

  const connectImap = async () => {
    if (!draft || busy || !formValid) return;
    setBusy(true);
    const labels = [t("Проверяем IMAP"), t("Проверяем SMTP"), t("Отправляем тестовое письмо")];
    for (let i = 0; i < labels.length; i += 1) {
      setSteps(labels.map((label, j) => ({ label, state: j < i ? "ok" : j === i ? "run" : "wait" })));
      await wait(620);
    }
    setSteps(labels.map((label) => ({ label, state: "ok" })));
    await wait(340);
    finish(draft, mail.trim());
  };

  const disconnect = (p: Provider) => {
    setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, connected: false, account: "", unread: 0 } : x)));
    flash(t("{name}: ящик отключён, история писем сохранена в архиве", { name: p.name }));
  };

  const validHost = (v: string) => v.trim().length > 2 && !v.includes(" ");
  const portOk = (v: string) => /^\d{2,5}$/.test(v.trim());
  const formValid =
    draft?.kind === "imap" &&
    emailOk(mail) &&
    pass.trim().length >= 4 &&
    validHost(hosts.imap) &&
    validHost(hosts.smtp) &&
    portOk(hosts.imapPort) &&
    portOk(hosts.smtpPort);

  return (
    <Band id="email" tone="default">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">{str(brand) || "Hub"} / {t("настройки → каналы → почта")}</span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Ящиков подключено {n} из {total}", { n: connected.length, total: items.length })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[17rem_minmax(0,1fr)]">
          {/* ── подключённые ящики ── */}
          <div className="hidden flex-col gap-3 border-border p-3 lg:flex lg:border-e">
            <p className="px-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Почтовые ящики")}</p>

            <div className="flex flex-col gap-2">
              {connected.length === 0 ? (
                <p className="rounded-btn border border-dashed border-border px-3 py-4 text-[11px] leading-relaxed text-muted-foreground">
                  {t("Пока ни одного ящика. Выберите провайдера справа — подключение займёт минуту.")}
                </p>
              ) : null}
              {connected.map((p) => (
                <div key={p.id} className="rounded-btn border border-border bg-background/50 p-2.5">
                  <div className="flex items-center gap-2">
                    <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-base leading-none text-primary" aria-hidden>
                      <i className={p.icon} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[12px] font-semibold">{p.account}</p>
                      <p className="truncate text-[10px] text-muted-foreground">{p.name}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => disconnect(p)}
                      aria-label={t("Отключить ящик {account}", { account: p.account })}
                      className="grid size-7 flex-none place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-red-400"
                    >
                      <X className="size-3.5" aria-hidden />
                    </button>
                  </div>
                  <p className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <span className="size-1.5 rounded-full bg-primary" aria-hidden />
                    {t("Активен")} · {p.unread > 0 ? tn(p.unread, "новое письмо", "новых письма", "новых писем") : t("новых писем нет")}
                  </p>
                </div>
              ))}
            </div>

            <div className="rounded-btn border border-border bg-background/60 p-3">
              <div className="flex items-center justify-between text-[11px] font-semibold">
                <span>{t("Ящиков на тарифе")}</span>
                <span className="tabular-nums text-muted-foreground">{connected.length} / 5</span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
                <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${Math.min(100, (connected.length / 5) * 100)}%` }} />
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{t("Каждый ящик — отдельный поток писем во «Входящих».")}</p>
            </div>

            <div className="mt-1 rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-3.5" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Пароли приложений видит только администратор. Продавцы получают письма, а не доступ к ящику.")}
              </p>
            </div>
          </div>

          {/* ── каталог провайдеров ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-col gap-3">
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {emailCategories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCat(c.id)}
                    aria-pressed={cat === c.id}
                    className={cn(
                      "inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[11px] font-semibold transition",
                      cat === c.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <i className={c.icon} aria-hidden />
                    {c.label}
                    <span className="tabular-nums opacity-70">{countIn(c.id)}</span>
                  </button>
                ))}
              </div>

              <label className="relative block">
                <span className="sr-only">{t("Поиск по почтовым провайдерам")}</span>
                <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Найти провайдера: Gmail, Яндекс, Outlook…")}
                  className="h-10 w-full box-border rounded-btn border border-border bg-background ps-9 pe-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
              </label>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {visible.map((p) => (
                <article
                  key={p.id}
                  className={cn(
                    "flex flex-col gap-3 rounded-card border p-4 transition",
                    p.connected ? "border-primary/45 bg-primary/5" : "border-border bg-background/40 hover:border-primary/40",
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={cn(
                        "grid size-10 flex-none place-items-center rounded-btn text-xl leading-none",
                        p.connected ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
                      )}
                      aria-hidden
                    >
                      <i className={p.icon} />
                    </span>
                    <span className="flex flex-wrap items-center justify-end gap-1">
                      {p.badge ? (
                        <span className="rounded-full bg-background/70 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground ring-1 ring-border">{p.badge}</span>
                      ) : null}
                      {p.connected ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                          <Check className="size-2.5" strokeWidth={3} aria-hidden />
                          {t("Подключено")}
                        </span>
                      ) : null}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold leading-snug">{p.name}</h3>
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{p.description}</p>
                  </div>

                  <div className="mt-auto flex items-center justify-between gap-2 border-t border-border/60 pt-2.5">
                    <span className="min-w-0 truncate text-[11px] text-muted-foreground">{p.connected ? p.account : t("Не подключён")}</span>
                    {p.connected ? (
                      <button
                        type="button"
                        onClick={() => disconnect(p)}
                        className="flex-none whitespace-nowrap rounded-btn border border-border px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground transition hover:border-red-400/60 hover:text-red-400"
                      >
                        {t("Отключить")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => open(p)}
                        className="flex-none whitespace-nowrap rounded-btn bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground transition hover:brightness-110"
                      >
                        {t("Подключить")}
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>

            {visible.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">{t("Провайдер не найден. Попробуйте другое название или категорию.")}</p>
            ) : null}

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Check className="size-3.5 text-primary" strokeWidth={3} aria-hidden />
                {t("Письма, вложения и цепочки — в одном диалоге")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="size-3.5 text-primary" strokeWidth={3} aria-hidden />
                {t("Ответ уходит с адреса ящика")}
              </span>
            </div>

            <p className="mt-3 text-[11px] text-muted-foreground">{t("Демо-режим: подключение имитируется в макете, реальные ящики не затрагиваются.")}</p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-10 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── окно подключения ящика ── */}
        {draft ? (
          <div className="absolute inset-0 z-20 flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (draft.kind === "imap") void connectImap();
                else void connectOauth();
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="email-dialog-title"
              className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift md:p-6"
            >
              <button
                type="button"
                onClick={close}
                disabled={busy}
                aria-label={t("Закрыть окно подключения почты")}
                className="absolute end-3 top-3 grid size-8 place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-40"
              >
                <X className="size-4" aria-hidden />
              </button>

              <div className="flex items-start gap-3 pe-8">
                <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                  <i className={draft.icon} />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {draft.kind === "imap" ? t("IMAP и SMTP") : t("Авторизация у провайдера")}
                  </p>
                  <h3 id="email-dialog-title" className="mt-1 text-lg font-semibold leading-snug">
                    {t("Подключить {name}", { name: draft.name })}
                  </h3>
                </div>
              </div>

              {draft.kind === "oauth" ? (
                <div className="mt-4 flex flex-col gap-3">
                  <div className="rounded-btn border border-border bg-background/60 p-3.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Hub запросит доступ")}</p>
                    <ul className="mt-2 flex flex-col gap-2">
                      {draft.permissions.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-[13px] leading-snug">
                          <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="flex items-start gap-2 text-[12px] leading-relaxed text-muted-foreground">
                    <ShieldCheck className="mt-0.5 size-4 flex-none text-primary" aria-hidden />
                    {t("Hub не сможет удалять письма или менять пароль. Отключить доступ можно в любой момент в настройках ящика.")}
                  </p>
                </div>
              ) : (
                <div className="mt-4 flex flex-col gap-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Адрес почты")}</span>
                      <input
                        type="email"
                        value={mail}
                        onChange={(e) => setMail(e.target.value)}
                        placeholder={draft.sample}
                        autoComplete="off"
                        className={FIELD}
                      />
                    </label>
                    <label className="flex min-w-0 flex-col gap-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Пароль приложения")}</span>
                      <input
                        type="password"
                        value={pass}
                        onChange={(e) => setPass(e.target.value)}
                        placeholder={t("Пароль или токен")}
                        autoComplete="new-password"
                        className={FIELD}
                      />
                    </label>
                    <label className="flex min-w-0 flex-col gap-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Имя отправителя")}</span>
                      <input value={sender} onChange={(e) => setSender(e.target.value)} placeholder={t("Отдел продаж")} autoComplete="off" className={FIELD} />
                    </label>

                    <div className="grid gap-1.5 sm:col-span-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Сервер IMAP")}</span>
                      <div className="flex gap-2">
                        <input
                          value={hosts.imap}
                          onChange={(e) => setHosts((h) => ({ ...h, imap: e.target.value }))}
                          placeholder="imap.your-domain.ru"
                          autoComplete="off"
                          className={cn(FIELD, "flex-1")}
                        />
                        <input
                          value={hosts.imapPort}
                          onChange={(e) => setHosts((h) => ({ ...h, imapPort: e.target.value }))}
                          inputMode="numeric"
                          placeholder="993"
                          className={cn(FIELD, "w-20 flex-none tabular-nums")}
                          aria-label={t("Порт IMAP")}
                        />
                      </div>
                    </div>

                    <div className="grid gap-1.5 sm:col-span-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Сервер SMTP")}</span>
                      <div className="flex gap-2">
                        <input
                          value={hosts.smtp}
                          onChange={(e) => setHosts((h) => ({ ...h, smtp: e.target.value }))}
                          placeholder="smtp.your-domain.ru"
                          autoComplete="off"
                          className={cn(FIELD, "flex-1")}
                        />
                        <input
                          value={hosts.smtpPort}
                          onChange={(e) => setHosts((h) => ({ ...h, smtpPort: e.target.value }))}
                          inputMode="numeric"
                          placeholder="465"
                          className={cn(FIELD, "w-20 flex-none tabular-nums")}
                          aria-label={t("Порт SMTP")}
                        />
                      </div>
                    </div>

                    <label className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Шифрование")}</span>
                      <select value={secure} onChange={(e) => setSecure(e.target.value)} className={FIELD}>
                        {SECURE.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {steps.length > 0 ? (
                    <ul className="flex flex-col gap-1.5 rounded-btn border border-border bg-background/60 p-3">
                      {steps.map((s) => (
                        <li key={s.label} className="flex items-center gap-2 text-[12px]">
                          {s.state === "ok" ? (
                            <Check className="size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
                          ) : s.state === "run" ? (
                            <Loader2 className="size-3.5 flex-none animate-spin text-primary" aria-hidden />
                          ) : (
                            <span className="size-3.5 flex-none rounded-full border border-border" aria-hidden />
                          )}
                          <span className={cn("truncate", s.state === "wait" ? "text-muted-foreground" : "text-foreground")}>{s.label}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                      {t("Для Gmail, Яндекса и Mail.ru нужен пароль приложения, а не пароль от аккаунта. Пароль хранится в зашифрованном виде.")}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={close}
                  disabled={busy}
                  className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground disabled:opacity-40"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="submit"
                  disabled={busy || (draft.kind === "imap" && !formValid)}
                  className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                  {draft.kind === "imap"
                    ? busy
                      ? t("Проверяем…")
                      : t("Проверить и подключить")
                    : busy
                      ? t("Ждём подтверждения…")
                      : draft.authLabel || t("Продолжить")}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default EmailDemo;